import { Fragment, memo, ReactNode, Ref, useMemo } from 'react';
import styled from 'styled-components';

import {
  calculateExpandableScreenFlowWidth,
  calculateScreenFlowPadding,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import {
  PathHighlightInformation,
  ScreenFlowBranchConnectors,
  ScreenFlowBranchContainer,
  ScreenFlowBranchEmptySpacer,
  ScreenFlowConnector,
  ScreenFlowContainer,
  ScreenFlowStepContainer,
} from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow/ScreenFlowElements';
import {
  buildScreenFlowDimens,
  isScreenFlowStack,
  ScreenFlowDimens,
  StepPosition,
} from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow/utils';
import { InquiryTemplateVersionStepType } from 'dashboard/models/inquiry-template-version';
import { WorkflowStep, WorkflowStepType } from 'dashboard/models/workflow-version';
import { UiStepType } from 'lib/interfaces/StepsConfig';

const Centered = styled.div.attrs<{ height: number; width?: number }>(({ height, width }) => ({
  style: width != null ? { height, width } : { height },
}))<{
  height: number;
  width?: number;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

// Abstract interface of common props between ScreenFlow and ScreenAndConnector
interface ScreenFlowConsistentProps<Step> {
  /**
   * list of steps that will be walked iteratively.
   */
  steps: Step[];
  /**
   * previous step in the flow
   */
  previousStep?: Step;
  /**
   * if previous step is a conditional step, the index of the branch we are currently walking
   */
  previousStepBranchIndex?: number;
  /**
   * node token of nearest preceding branch node. used to generate unique ref IDs for screens and connectors
   */
  parentWorkflowNodeToken?: string;
  /**
   * height of a single rendered step
   */
  stepContentHeight: number;
  /**
   * width of a single rendered step
   */
  stepContentWidth: number;
  /**
   * function to change ScreenFlow color and visibility for
   * inquiry run visualization and flow tree selected branch nodes
   */
  pathHighlightInformationForStep?: (step: Step) => PathHighlightInformation;
  /**
   * function to render each step that takes `step` as an argument
   */
  renderStep: (
    step: Step,
    previousStep?: Step,
    previousStepBranchIndex?: number,
    nextStep?: Step,
    parentWorkflowNodeToken?: string,
  ) => ReactNode;
  /**
   * renders a connector between steps that takes "previous step" and "next step"
   * "next step" will be `undefined` when doesn't exist
   */
  renderConnector: (previousStep: Step, nextStep: Step) => ReactNode;
  /**
   * renders a connector on the root of a branch that takes "step" and "branch index"
   */
  renderBranchRootConnector: (
    branchRootStep: Step,
    branchIndex: number,
    runPathIndexes?: number[],
  ) => ReactNode;
}
interface ScreenFlowProps<Step> extends ScreenFlowConsistentProps<Step> {
  /**
   * ref for the root container. Couldn't use React.forwardRef instead to avoid unintuitive Generic wrapping
   */
  innerRef: Ref<HTMLDivElement>;
}

/**
 * A component to render steps with tree-ish layout
 */
const ScreenFlow = <Step extends { token: string; branches?: Step[][] }>({
  innerRef,
  steps,
  stepContentHeight,
  stepContentWidth,
  pathHighlightInformationForStep,
  renderStep,
  renderConnector,
  renderBranchRootConnector,
}: ScreenFlowProps<Step>) => {
  // Padding between screen flow cards should be 1/4 of the screen height
  const stepHeight = stepContentHeight + calculateScreenFlowPadding(stepContentHeight);
  const [stepTokenToPositionMap, screenFlowDimens]: [Map<string, StepPosition>, ScreenFlowDimens] =
    useMemo(() => {
      const map = new Map<string, StepPosition>();
      const screenFlowDimens = buildScreenFlowDimens(steps, stepHeight, stepContentWidth, map);
      return [map, screenFlowDimens];
    }, [steps, stepContentWidth, stepHeight]);

  return (
    <ScreenFlowContainer
      height={screenFlowDimens.height}
      width={screenFlowDimens.width}
      ref={innerRef}
    >
      <ScreenAndConnector
        steps={steps}
        stepContentHeight={stepHeight}
        stepContentWidth={stepContentWidth}
        stepTokenToPositionMap={stepTokenToPositionMap}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pathHighlightInformationForStep={pathHighlightInformationForStep}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        renderStep={renderStep}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        renderConnector={renderConnector}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        renderBranchRootConnector={renderBranchRootConnector}
      />
    </ScreenFlowContainer>
  );
};

interface ScreenAndConnectorProps<Step> extends ScreenFlowConsistentProps<Step> {
  stepTokenToPositionMap: Map<string, StepPosition>;
}

const ScreenAndConnector = memo(
  <
    Step extends {
      token: string;
      type?: InquiryTemplateVersionStepType | 'workflow';
      workflowStepType?: WorkflowStep['type'];
      branches?: Step[][];
      isConnector?: boolean;
    },
  >(
    props: ScreenAndConnectorProps<Step>,
  ) => {
    const {
      steps,
      stepContentHeight,
      stepContentWidth,
      stepTokenToPositionMap,
      previousStep,
      previousStepBranchIndex,
      parentWorkflowNodeToken,
      pathHighlightInformationForStep,
      renderStep,
      renderConnector,
      renderBranchRootConnector,
    } = props;
    const [currentStep, ...nextSteps] = steps;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const { height, connectorWidth } = stepTokenToPositionMap.get(currentStep.token);
    const { branchColor, highlightColor, highlightPercentage, runPathIndexes, branchStrokeWidth } =
      pathHighlightInformationForStep?.(currentStep) ?? {};

    const connectorAfterStep = () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (currentStep.branches?.length > 0) {
        return <></>;
      }

      if (currentStep.type === UiStepType.Complete || currentStep.isConnector) {
        return <></>;
      }

      return (
        renderConnector(currentStep, nextSteps[0]) ?? (
          <ScreenFlowConnector height={stepContentHeight} width={connectorWidth} />
        )
      );
    };

    const renderAfterStep = () => {
      return (
        <>
          {currentStep.workflowStepType !== WorkflowStepType.Conditional && connectorAfterStep()}
          {/* next step */}
          {nextSteps.length > 0 && (
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            <ScreenAndConnector
              {...props}
              previousStep={currentStep}
              previousStepBranchIndex={undefined}
              parentWorkflowNodeToken={parentWorkflowNodeToken}
              steps={nextSteps}
            />
          )}
        </>
      );
    };

    return (
      <ScreenFlowStepContainer height={height}>
        {/* current step */}
        <Centered
          height={stepContentHeight}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          width={
            isScreenFlowStack(currentStep)
              ? calculateExpandableScreenFlowWidth(stepContentWidth)
              : null
          }
        >
          {renderStep(
            currentStep,
            previousStep,
            previousStepBranchIndex,
            // if this is a branching node, pass the 'next step' for delete node purposes as the first branch token
            // delete workflow steps uses current step to determine what to connect to, but deleting screen steps connects prev step to next step
            // so this is only really needed for the case of multiple transition componenents leading to branching screen step
            nextSteps[0] ?? currentStep?.branches?.[0][0],
            parentWorkflowNodeToken,
          )}
        </Centered>
        {currentStep?.branches?.length && (
          <>
            <ScreenFlowConnector
              height={stepContentHeight}
              width={connectorWidth}
              // highlightColor takes precedence
              color={highlightColor || branchColor}
              strokeWidth={branchStrokeWidth}
            />
            <ScreenFlowBranchConnectors
              height={stepContentHeight}
              currentToken={currentStep.token}
              runPathIndexes={runPathIndexes}
              color={branchColor}
              highlightColor={highlightColor}
              highlightPercentage={highlightPercentage}
              strokeWidth={branchStrokeWidth}
              childTokens={currentStep.branches.map(
                // blank token is to render connector to empty branch
                (branchSteps) => (branchSteps.length > 0 ? branchSteps[0].token : ''),
              )}
            />
            <ScreenFlowBranchContainer height={stepContentHeight}>
              {currentStep.branches.map((branchSteps, branchIndex) => {
                const branchRootConnector = renderBranchRootConnector(
                  currentStep,
                  branchIndex,
                  runPathIndexes,
                );
                const spaceWidth = branchSteps.length
                  ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    stepTokenToPositionMap.get(branchSteps[0].token).height
                  : stepContentHeight;
                return (
                  <Fragment key={branchIndex}>
                    {branchRootConnector && (
                      <ScreenFlowStepContainer height={height}>
                        <Centered height={stepContentHeight}>{branchRootConnector}</Centered>
                        {branchSteps.length ? (
                          <ScreenAndConnector
                            {...props}
                            previousStep={currentStep}
                            previousStepBranchIndex={branchIndex}
                            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                            parentWorkflowNodeToken={
                              currentStep.type === 'workflow' ? currentStep.token : null
                            }
                            steps={branchSteps}
                          />
                        ) : (
                          <ScreenFlowBranchEmptySpacer height={spaceWidth} />
                        )}
                      </ScreenFlowStepContainer>
                    )}
                  </Fragment>
                );
              })}
            </ScreenFlowBranchContainer>
          </>
        )}
        {renderAfterStep()}
      </ScreenFlowStepContainer>
    );
  },
);
ScreenAndConnector.displayName = 'ScreenAndConnector';

export default ScreenFlow;
